import { Head } from "@inertiajs/react"
import { AlertCircle, FileText, Server as ServerIcon } from "lucide-react"

import Heading from "@/components/heading"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import AppLayout from "@/layouts/app-layout"
import { serverPath, serversPath } from "@/routes"
import type { BreadcrumbItem, Server } from "@/types"

interface ConfigFile {
  filename: string
  content: string
  parse_error?: string
  enabled?: boolean
}

interface ServerConfigFilesViewerProps {
  server: Pick<Server, "id" | "name" | "hostname">
  configs: ConfigFile[]
  pageTitle: string
  headingTitle: string
  directoryPath: string
  breadcrumbTitle: string
}

export default function ServerConfigFilesViewer({
  server,
  configs,
  pageTitle,
  headingTitle,
  directoryPath,
  breadcrumbTitle,
}: ServerConfigFilesViewerProps) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Servers", href: serversPath() },
    { title: server.name, href: serverPath(server.id) },
    { title: breadcrumbTitle, href: "" },
  ]

  const hasConfigs = configs.length > 0
  const hasErrors = configs.some((config) => config.parse_error)
  const hasEnabledInfo = configs.some((config) => config.enabled !== undefined)
  const enabledCount = configs.filter((config) => config.enabled).length

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${server.name} - ${pageTitle}`} />

      <div className="p-6">
        <div className="flex items-center justify-between">
          <Heading
            title={headingTitle}
            description={`Configuration files from ${directoryPath} on ${server.name}`}
          />
        </div>

        {hasErrors && (
          <Alert variant="destructive" className="mt-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Some configuration files could not be read due to permission
              errors.
            </AlertDescription>
          </Alert>
        )}

        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ServerIcon className="h-5 w-5" />
              {server.name}
            </CardTitle>
            <CardDescription>
              {hasConfigs ? (
                <>
                  Found {configs.length} configuration file
                  {configs.length === 1 ? "" : "s"}
                  {hasEnabledInfo &&
                    ` (${enabledCount} active, ${configs.length - enabledCount} inactive)`}
                </>
              ) : (
                "No configuration files found"
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!hasConfigs ? (
              <div className="text-muted-foreground flex flex-col items-center justify-center py-12">
                <FileText className="h-12 w-12" />
                <p className="mt-4 text-sm">
                  No configuration files found in {directoryPath}
                </p>
              </div>
            ) : (
              <Tabs defaultValue={configs[0]?.filename} className="w-full">
                <TabsList className="mb-4 h-auto flex-wrap justify-start">
                  {configs.map((config) => (
                    <TabsTrigger key={config.filename} value={config.filename} className="justify-start">
                      <FileText className="mr-2 h-4 w-4" />
                      {config.filename}
                      {config.enabled && (
                        <Badge
                          variant="default"
                          className="ml-2 bg-green-600 hover:bg-green-700"
                        >
                          Active
                        </Badge>
                      )}
                      {config.parse_error && (
                        <AlertCircle className="text-destructive ml-2 h-3 w-3" />
                      )}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {configs.map((config) => (
                  <TabsContent key={config.filename} value={config.filename}>
                    {config.parse_error ? (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          Error reading file: {config.parse_error}
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <div className="space-y-4">
                        {config.enabled !== undefined && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">Status:</span>
                            <Badge
                              variant={config.enabled ? "default" : "secondary"}
                              className={
                                config.enabled
                                  ? "bg-green-600 hover:bg-green-700"
                                  : ""
                              }
                            >
                              {config.enabled ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                        )}
                        <div className="bg-muted/50 rounded-lg border p-4">
                          <pre className="overflow-x-auto text-sm">
                            <code>{config.content || "(empty file)"}</code>
                          </pre>
                        </div>
                      </div>
                    )}
                  </TabsContent>
                ))}
              </Tabs>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
